import type { RandomGenerator } from './hand-sampler';

export interface ScoredCandidate<T> {
  item: T;
  score: number;
}

export interface NearEqualSelection<T> {
  item: T;
  nearEqualCount: number;
}

/**
 * Select stochastically among candidates within tolerance of the best score.
 * Temperature controls how flat the distribution is among near-tied moves.
 */
export function selectNearEqual<T>(
  candidates: ScoredCandidate<T>[],
  tolerance: number,
  temperature: number,
  rng: RandomGenerator,
): T {
  return selectNearEqualWithMetadata(candidates, tolerance, temperature, rng)
    .item;
}

export function selectNearEqualWithMetadata<T>(
  candidates: ScoredCandidate<T>[],
  tolerance: number,
  temperature: number,
  rng: RandomGenerator,
): NearEqualSelection<T> {
  if (candidates.length === 0) {
    throw new Error('selectNearEqual: no candidates');
  }

  const sorted = [...candidates].sort((a, b) => b.score - a.score);
  const bestScore = sorted[0]!.score;
  const nearEqual = sorted.filter(
    (candidate) => bestScore - candidate.score <= tolerance,
  );

  if (nearEqual.length === 1 || temperature <= 0) {
    return { item: nearEqual[0]!.item, nearEqualCount: nearEqual.length };
  }

  const weights = nearEqual.map((candidate) =>
    Math.exp((candidate.score - bestScore) / Math.max(temperature, 1e-6)),
  );
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let roll = rng.random() * total;

  for (let index = 0; index < nearEqual.length; index += 1) {
    roll -= weights[index]!;
    if (roll <= 0) {
      return { item: nearEqual[index]!.item, nearEqualCount: nearEqual.length };
    }
  }

  return {
    item: nearEqual[nearEqual.length - 1]!.item,
    nearEqualCount: nearEqual.length,
  };
}

export function scoreToleranceForTemperature(
  temperature: number,
  scale: number,
): number {
  return Math.max(scale * 0.02, temperature * scale);
}
